import { Parent_Type_Enum } from '@risksmart-app/web-graphql-client/generated/graphql';
import { t } from 'i18next';

/**
 * A risk's parent is held on the `ParentRiskId` column and only projected into the
 * `linked_item` table as a `parent_child` / `child_parent` row by DB triggers. Risk
 * parenting is deliberately column-based (changed via the Parent risk field on the
 * record), so the generic unlink path cannot remove these rows — the backend rejects
 * the attempt, which previously surfaced as the misleading "Failed to remove links" /
 * "Failed to save changes" toasts (COI-521).
 *
 * This is specifically the risk↔risk tier relationship: both ends are risks. A
 * `parent_child` / `child_parent` row between two risks can only be the tier projection,
 * because `classifyLink` always classifies same-type links as `sibling` — the only way
 * two risks get a parent/child row is the `ParentRiskId` trigger.
 *
 * BOTH ends must be checked. The Linked Items query filters `Source = <viewed entity>`
 * and exposes no source type, so the source's type comes from `parentType` (the viewed
 * record). Checking the target alone would wrongly fire on other tabs: a risk is the
 * hierarchy parent of controls/actions/indicators/acceptances/appetites, so on (say) a
 * control's tab the linked parent risk appears as a `child_parent` row whose target is a
 * risk — but unlinking a control from a risk IS supported (`control` is a parent-table
 * entity), so that must not be blocked.
 */
export const isRiskParentLink = (
  item: {
    RelationshipType?: string | null;
    target_risk?: unknown;
  },
  parentType: Parent_Type_Enum | undefined
): boolean =>
  parentType === Parent_Type_Enum.Risk &&
  !!item.target_risk &&
  (item.RelationshipType === 'parent_child' ||
    item.RelationshipType === 'child_parent');

export const getUnlinkFriendlyErrorMessage = (errorMessage: string): string => {
  // @TODO: this is temporary, remove once we've implemented supported for all object types
  const types = errorMessage.match(/(?<=\{).*?(?=\})/g);
  if (types && types?.length >= 2) {
    const sourceType = t(
      // @ts-ignore
      `${types[0]}_other`
    );
    const targetType = t(
      // @ts-ignore
      `${types[1]}_other`
    );

    // TODO: Translation
    return `Removing links between ${sourceType} and ${targetType} is not supported.`;
  } else {
    // TODO: Translation
    return 'Failed to remove links';
  }
};
